import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Star, Phone, MessageCircle, FileText, MapPin, Wallet } from "lucide-react";
import { ErrorScreen } from "@/components/AppErrorBoundary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Profil public d'un artisan : métiers, avis, contact (appel, WhatsApp, demande de devis)
export default function ArtisanPublicProfile({ user }) {
  const { artisanId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [specs, setSpecs] = useState([]);
  const [error, setError] = useState(null);

  // Connecté : ouvrir la fiche consomme 1 consultation du pass (100 FCFA = 5 fiches)
  useEffect(() => {
    const req = user
      ? axios.post(`${API}/artisans/profile/${artisanId}/unlock`)
      : axios.get(`${API}/artisans/profile/${artisanId}`);
    req.then((r) => {
      setProfile(r.data);
      if (r.data.charged) toast.success(`100 FCFA débités · ${r.data.views_left} fiche(s) restante(s)`);
    }).catch((e) => setError({ status: e.response?.status, detail: e.response?.data?.detail }));
    axios.get(`${API}/artisans/specialties`).then((r) => setSpecs(r.data)).catch(() => {});
  }, [artisanId, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error?.status === 402 || error?.status === 401) return (
    <div className="min-h-screen bg-[#0F172A] text-white flex items-center justify-center px-6" data-testid="artisan-profile-paywall">
      <div className="max-w-md text-center space-y-5">
        <Wallet className="mx-auto text-[#D4AF37]" size={36} />
        <p className="text-sm text-white/80" data-testid="artisan-profile-paywall-msg">{error.detail}</p>
        <div className="flex justify-center gap-3">
          <button type="button" onClick={() => navigate(-1)} className="rounded-full bg-white/10 px-5 py-3 text-xs font-black uppercase tracking-widest" data-testid="artisan-paywall-back-btn">Retour</button>
          <button type="button" onClick={() => navigate(error.status === 401 ? "/auth" : "/credits")} className="rounded-full bg-[#D4AF37] text-[#0F172A] px-5 py-3 text-xs font-black uppercase tracking-widest" data-testid="artisan-paywall-recharge-btn">
            {error.status === 401 ? "Se connecter" : "Recharger via Wave"}
          </button>
        </div>
      </div>
    </div>
  );
  if (error) return <ErrorScreen title="Artisan introuvable" message="Ce profil n'existe plus ou n'est plus disponible." />;
  if (!profile) return <div className="min-h-screen bg-[#0F172A] flex items-center justify-center text-white/60">Chargement…</div>;

  const phone = (profile.phone || "").replace(/\D/g, "");
  const wa = phone ? `https://wa.me/${phone.startsWith("221") ? phone : `221${phone}`}` : null;
  const labels = (profile.specialties || []).map((k) => specs.find((s) => s.key === k)?.label || k);

  return (
    <div className="min-h-screen bg-[#0F172A] text-white" data-testid="artisan-public-profile">
      <div className="max-w-2xl mx-auto px-5 py-8 space-y-6">
        <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-white/60 hover:text-white" data-testid="artisan-profile-back-btn">
          <ArrowLeft size={14} /> Retour
        </button>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 border-2 border-[#D4AF37] overflow-hidden flex items-center justify-center text-2xl font-black">
            {profile.avatar_url ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" /> : (profile.full_name || "A")[0]}
          </div>
          <div>
            <h1 className="text-2xl font-black" data-testid="artisan-profile-name">{profile.full_name || "Artisan"}</h1>
            <p className="text-sm text-[#D4AF37]" data-testid="artisan-profile-specialties">{labels.join(" · ")}</p>
            <p className="text-xs text-white/60 flex items-center gap-1 mt-1">
              <Star size={12} className="text-[#D4AF37]" /> {(profile.rating || 0).toFixed(1)} ({profile.total_ratings || 0} avis) · {profile.completed_projects || 0} chantiers
            </p>
          </div>
        </div>
        {profile.bio && <p className="text-sm text-white/80 leading-relaxed" data-testid="artisan-profile-bio">{profile.bio}</p>}
        <div className="grid grid-cols-2 gap-3 text-sm">
          {profile.hourly_rate > 0 && <div className="bg-white/5 rounded-2xl p-3">Tarif horaire<br /><b>{profile.hourly_rate} FCFA</b></div>}
          <div className="bg-white/5 rounded-2xl p-3 flex items-center gap-2"><MapPin size={14} /> Rayon {profile.service_radius_km || 15} km</div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={() => navigate(user ? `/artisans/new?category=${profile.specialties?.[0] || ""}&artisan=${artisanId}` : "/auth")} className="rounded-full bg-[#D4AF37] text-[#0F172A] px-5 py-3 text-xs font-black uppercase tracking-widest flex items-center gap-2" data-testid="artisan-profile-quote-btn">
            <FileText size={14} /> Demander un devis
          </button>
          {phone && <a href={`tel:+${phone.startsWith("221") ? phone : `221${phone}`}`} className="rounded-full bg-white/10 px-5 py-3 text-xs font-black uppercase tracking-widest flex items-center gap-2" data-testid="artisan-profile-call-btn"><Phone size={14} /> Appeler</a>}
          {wa && <a href={wa} target="_blank" rel="noopener noreferrer" className="rounded-full bg-emerald-500 text-white px-5 py-3 text-xs font-black uppercase tracking-widest flex items-center gap-2" data-testid="artisan-profile-whatsapp-btn"><MessageCircle size={14} /> WhatsApp</a>}
          {profile.locked && (
            <button type="button" onClick={() => navigate("/auth")} className="rounded-full bg-white/10 px-5 py-3 text-xs font-black uppercase tracking-widest" data-testid="artisan-profile-login-btn">
              Se connecter pour voir les coordonnées
            </button>
          )}
        </div>
        {typeof profile.views_left === "number" && (
          <p className="text-xs text-white/50" data-testid="artisan-views-left">
            Pass artisans : {profile.views_left} fiche(s) restante(s) · 100 FCFA = 5 fiches
          </p>
        )}
      </div>
    </div>
  );
}
